import axios from 'axios';
import L, { geoJSON } from 'leaflet';
import { MapInstance } from 'src/app/redux/map/map.redux.type';
import {
  ITileLayer,
  IVectorLayer,
  IWMSLayer,
  TLayers,
  defaultStyle,
  getCRSFromString,
  s57LayerPolygonStyle,
  s57LayerStyle,
} from 'src/app/services/layersService/layers-server.service.config';
import { finalUrlsConfig } from 'src/configs/url.config';

const vectorLayersSources: Record<string, L.GeoJSON> = {};

const tileLayersSources: Record<string, L.TileLayer> = {};

let layerControl: L.Control | undefined;

export class LayersController {
  // minimapLayer: L.TileLayer | undefined;

  axiosInstance = axios.create({
    baseURL: finalUrlsConfig().fileServerBaseURL,
  });

  layersSource: TLayers = [];

  activeLayer: string | undefined;

  static instance: LayersController | null = null;

  static getInstance(): LayersController {
    if (this.instance === null) {
      this.instance = new LayersController();
    }
    return this.instance;
  }

  /*
    Here we get all layers sources, and if there is an active layer we set the active layer.
  */
  setLayersSource = async (data: TLayers, map: L.Map, mapId: MapInstance) => {
    this.layersSource = data;

    this.setTileLayers(
      this.layersSource.filter((o) => o.layerType === 'tile' && o.isBaseLayer && o.isActive) as ITileLayer[],
      this.layersSource.filter((o) => o.layerType === 'wms' && o.isBaseLayer && o.isActive) as IWMSLayer[],
    );

    this.fetchBaseVectors(
      this.layersSource.filter((o) => o.layerType === 'geojson' && o.isBaseLayer && o.isActive) as IVectorLayer[],
    );
    this.fetchOverlayVectors(
      this.layersSource.filter((o) => o.layerType === 'geojson' && !o.isBaseLayer && o.isActive) as IVectorLayer[],
      map,
      mapId,
    );

    if (this.activeLayer) {
      this.handleLayerChange(this.activeLayer, map);
    }
  };

  /*
    Base vector layers are shown in the right bottom corner of the map, with base tile layers,
    this function call is used to fetch the base vector layers, and add it to the layers
  */
  fetchBaseVectors = async (layers: IVectorLayer[]) => {
    for (const layer of layers) {
      try {
        if (layer.id) {
          let data;
          if (layer.filePath && layer.filePath !== '') {
            data = (await this.axiosInstance.get(`${finalUrlsConfig().layersServerBaseURL}${layer.filePath}`)).data;
          } else if (layer.url) {
            data = (await this.axiosInstance.get(layer.url)).data;
          }

          if (data) {
            vectorLayersSources[layer.id] = geoJSON(data, {
              style: defaultStyle(false, layer.geometryColor),
              attribution: layer.attribution,
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch vector map');
      }
    }
  };

  fetchOverlayVectors = async (layers: IVectorLayer[], map: L.Map, mapId: MapInstance) => {
    const overlays: Record<string, L.GeoJSON> = {};

    for (const layer of layers) {
      if (layer.id && layer.filePath && layer.filePath !== '') {
        try {
          const { data } = await this.axiosInstance.get(`${finalUrlsConfig().layersServerBaseURL}${layer.filePath}`);

          if (data) {
            const style =
              layer.geometryType === 'Polygon'
                ? {
                    ...s57LayerPolygonStyle,
                    fillColor: layer.geometryColor,
                  }
                : {
                    ...s57LayerStyle,
                    fillColor: layer.geometryColor,
                  };

            let geojsonLayer;

            switch (layer.geometryType) {
              case 'Point':
                geojsonLayer = geoJSON(data, {
                  style,
                  // onEachFeature,
                  pointToLayer: (_feature, latlng) => {
                    return L.circleMarker(latlng, {
                      ...style,
                      radius: 4,
                    });
                  },
                });

                break;

              case 'Polygon':
                geojsonLayer = geoJSON(data, {
                  // onEachFeature,
                  style,
                });

                break;

              case 'Polyline':
                geojsonLayer = geoJSON(data, {
                  // onEachFeature,
                  style,
                });

                break;

              default:
                break;
            }

            if (geojsonLayer) {
              overlays[layer.label] = geojsonLayer;
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch vector map for: ', layer.label);
        }
      }
    }

    if (Object.keys(overlays).length && mapId === MapInstance.DEFAULT) {
      layerControl?.remove();
      layerControl = L.control.layers({}, overlays, { collapsed: false });
      layerControl.addTo(map);
    }
  };

  setTileLayers = (tileLayers: ITileLayer[], wmsLayers: IWMSLayer[]) => {
    tileLayers.forEach((layerDetails) => {
      if (layerDetails.id) {
        const { url } = layerDetails;
        const options = {
          edgeBufferTiles: 1,
          attribution: layerDetails.attribution,
        };
        const existingLayer = tileLayersSources[layerDetails.id];
        if (existingLayer) {
          existingLayer.remove();
        }
        tileLayersSources[layerDetails.id] = L.tileLayer(url, options) as L.TileLayer;
      }
    });

    wmsLayers.forEach((layerDetails) => {
      if (layerDetails.id) {
        const { url } = layerDetails;

        const options: L.WMSOptions & { tiled?: string } = {
          layers: layerDetails.wmsLayer,
          format: layerDetails.format,
          transparent: layerDetails.transparent,
          version: layerDetails.version,
          crs: getCRSFromString(layerDetails.crs),
          attribution: layerDetails.attribution,
        };

        if (layerDetails.tiled) {
          options.tiled = 'true';
        }

        const existingLayer = tileLayersSources[layerDetails.id];
        if (existingLayer) {
          existingLayer.remove();
        }

        tileLayersSources[layerDetails.id] = L.tileLayer.wms(url, options);
      }
    });
  };

  toggleVectorLayer = (id: string, onOff: boolean, map: L.Map) => {
    if (vectorLayersSources) {
      if (onOff) {
        if (!map.hasLayer(vectorLayersSources[id])) {
          vectorLayersSources[id]?.addTo(map).bringToBack();
        }
      } else {
        vectorLayersSources[id]?.remove();
      }
    }
  };

  toggleTileLayer = (id: string, onOff: boolean, map: L.Map) => {
    if (tileLayersSources[id] !== undefined) {
      if (onOff) {
        if (!map.hasLayer(tileLayersSources[id])) {
          tileLayersSources[id].addTo(map).bringToBack();
        } else {
          tileLayersSources[id].remove();
        }
      } else {
        tileLayersSources[id].remove();
      }
    }
  };

  removeBaseLayers = () => {
    Object.entries(vectorLayersSources).forEach(([_key, layer]) => {
      layer.remove();
    });

    Object.entries(tileLayersSources).forEach(([_key, layer]) => {
      layer.remove();
    });
  };

  // Finally this function, removes all other layers from the map,
  // and adds only selected layer
  handleLayerChange = (id: string, map: L.Map) => {
    const layer = this.layersSource.find((o) => o.id === id);
    const layerToActivate = vectorLayersSources[id] || tileLayersSources[id];

    if (layerToActivate !== undefined) {
      this.removeBaseLayers();
      Object.values(vectorLayersSources).forEach((layerToRemove) => layerToRemove.remove());
      Object.values(tileLayersSources).forEach((layerToRemove) => layerToRemove.remove());

      this.activeLayer = id;
      layerToActivate.addTo(map).bringToBack();
    }
    if (layer?.layerType === 'geojson') {
      // this.toggleMinimap(false, map, id);
      layerToActivate?.bringToBack();
    } else {
      // this.changeMinimapUrl(id);
      // this.toggleMinimap(true, map, id);
    }
  };
}
